/**
 * CardHoldExplainer.tsx — at the card step, what the hold is and what happens to
 * it (CTO, 2026-09-11: a hold, not a charge, and if the medication changes or the
 * patient does not qualify they are only charged the actual amount).
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * HOLD ROUTE ONLY. On the clinician-led route the plan is already approved and
 * confirming takes the payment, so "you don't pay today" would be false there.
 *
 * Every sentence lives in `paymentCopy.ts` (`cardHoldExplainerCopy`), beside the
 * orders-service behaviour it describes. This file only lays it out.
 *
 * "pre-authorisation" never splits at its hyphen (review, 2026-09-25): in the
 * two-column rail it broke as "pre-" / "authorisation" at 1440x900, the same
 * complaint as the CTO's "weigh-loss". `KeepCompoundsWhole` only decides where a
 * line may end; the words are unchanged.
 *
 * ICON-LED (2026-09-26, Ashley Haveloch-Jones via the CTO: "too much text"; the
 * CTO: "use icons more where ever possible"). The facts are the shared
 * `IconPoints` list — the same look as "What happens next" and, since the same
 * day, the plan screen's "What you're agreeing to" — each led by a decorative
 * glyph: a stethoscope (the approval), a circular arrow (a change of
 * treatment). The icon never carries the fact.
 *
 * TWO POINTS, NOT THREE (2026-09-26, later — the CTO: "we are saying the same
 * thing over and over"). The first point, "You don't pay today. We just hold £X
 * (your first month) on your card.", restated the order summary right above
 * it: "Held on your card today £X (your first month)". The summary keeps the
 * figure; this box keeps its heading ("This is a hold, not a payment"), the
 * approval point and the treatment-change point, and prints no figure at all.
 * With no price to print, the summary's headline says "Your first month's
 * price" in words (`PaymentRail` `HELD_WITHOUT_FIGURE`), so the screen still
 * says what is held today.
 */
import { useId, type ReactElement } from 'react';
import { IconRefresh, IconStethoscope } from '../ui/icons';
import { IconPoints } from '../ui/IconPoints';
import { KeepCompoundsWhole } from '../ui/KeepCompoundsWhole';
import { cardHoldExplainerCopy, type CardHoldPoint } from './paymentCopy';

const POINT_ICONS: Record<CardHoldPoint['key'], () => ReactElement> = {
  approval: () => <IconStethoscope />,
  change: () => <IconRefresh />,
};

export function CardHoldExplainer() {
  const headingId = useId();
  const copy = cardHoldExplainerCopy();
  return (
    <section
      aria-labelledby={headingId}
      // `gps-jux-panel`: the journey's one white card (tokens.css, P7).
      className="gps-jux-panel"
      style={{
        marginBottom: 'var(--gps-s-3)',
        padding: '16px 18px',
        background: 'var(--gps-surface-2)',
        border: '1px solid var(--gps-border)',
        borderRadius: 'var(--gps-r-lg)',
        color: 'var(--gps-text)',
      }}
    >
      <h2 id={headingId} style={{ margin: '0 0 12px', fontSize: '17px', fontWeight: 700 }}>
        {copy.heading}
      </h2>
      <IconPoints
        points={copy.points.map((point) => ({
          key: point.key,
          icon: POINT_ICONS[point.key](),
          title: <KeepCompoundsWhole text={point.lead} />,
          body: <KeepCompoundsWhole text={point.body} />,
        }))}
      />
    </section>
  );
}
